import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import _get from 'hs-lodash/get';
import { useContext, useEffect, useState } from 'react';
import { DEAL_CURRENCY_FIELD_NAME, LINKED_PROPERTIES_CONFIG } from '../../../constants/associations';
import { propertyKeyValidationTypes } from '../../../constants/properties';
import { singularTypeToPlural } from '../../../constants/types';
import { AppContext } from '../../../contexts/app/context';
import { AssociationModalPropsContext } from '../../../contexts/associationModal/PropsContext';
import { AssociationModalContext } from '../../../contexts/associationModal/context';
import { PropertiesContext } from '../../../contexts/properties/context';
import ApiOutagePlaceholder from '../../common/ApiOutagePlaceholder/ApiOutagePlaceholder';
import FormInputs from '../../common/FormInputs/FormInputs';
import AssociationProperties from '../AssociationProperties/AssociationProperties';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function CreateNewTab({
  isApiOutageError,
  setIsApiOutageError
}) {
  const {
    crmObjectType,
    portalId,
    openUrl,
    apiWrapper,
    prosemirrorTrackerProperties,
    hubspotUserId
  } = useContext(AssociationModalPropsContext);
  const {
    formFields,
    hasMulticurrencyActive,
    crmObjectSearchProperties,
    crmObjectCreationFormErrorMessage,
    associationModalActions
  } = useContext(AssociationModalContext);
  const {
    propertiesActions
  } = useContext(PropertiesContext);
  const {
    appName
  } = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const loadingCallback = () => {
    setIsLoading(false);
  };
  useEffect(() => {
    setIsLoading(true);
    associationModalActions.getProperties({
      crmObjectType,
      callback: loadingCallback,
      errorCallback: () => setIsApiOutageError(true)
    });
    return () => {
      associationModalActions.resetSearchProperties();
      associationModalActions.resetNewLineItems();
      associationModalActions.resetCrmObjectAssociations();
      associationModalActions.resetFormError();
      propertiesActions.resetValidationErrors();
      setIsApiOutageError(false);
    };
  }, [associationModalActions, crmObjectType, propertiesActions, setIsApiOutageError]);
  useEffect(() => {
    if (crmObjectCreationFormErrorMessage) {
      setShowAlert(true);
    }
  }, [crmObjectCreationFormErrorMessage]);
  useEffect(() => setShowAlert(false), [formFields]);
  if (isApiOutageError) {
    return /*#__PURE__*/_jsx(ApiOutagePlaceholder, {
      title: I18n.text('home.generalError.description'),
      description: I18n.text('errors.generalHubSpotApiErrorDescription')
    });
  }

  // we can`t render form if we don`t have formFields to control data
  // as we set default values for selects in child components
  if (isLoading || !formFields.length) {
    return /*#__PURE__*/_jsx(LoadingOverlay, {
      contextual: true,
      size: "md"
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [crmObjectCreationFormErrorMessage && showAlert && /*#__PURE__*/_jsx(Alert, {
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "errors.objectCreate.title",
        options: {
          objectType: crmObjectType
        }
      }),
      type: "danger",
      closeable: true,
      onClose: () => setShowAlert(false),
      "data-test-id": `${appName}-fail-create-object-alert`,
      children: crmObjectCreationFormErrorMessage
    }), crmObjectSearchProperties.map(property => {
      const currentField = formFields.find(el => el.name === property.name);
      const correlatedField = currentField ? LINKED_PROPERTIES_CONFIG[singularTypeToPlural[crmObjectType]][currentField.name] : undefined;
      const correlatedFieldEntity = correlatedField ? formFields.find(el => el.name === correlatedField.relatedProperty) : '';
      const isError = !_get(currentField, 'valid');
      const fieldvalue = _get(currentField, 'value', '');
      const disabled = _get(currentField, 'disabled', false);
      const tooltip = _get(currentField, 'tooltip', '');
      const getErrorMessage = () => {
        if (currentField && currentField.value) {
          switch (property.name) {
            case propertyKeyValidationTypes.email:
              return propertyKeyValidationTypes.email;
            case propertyKeyValidationTypes.domain:
              return propertyKeyValidationTypes.domain;
            case propertyKeyValidationTypes.linkedin_company_page:
              return propertyKeyValidationTypes.linkedin_company_page;
            case propertyKeyValidationTypes.phone:
              return propertyKeyValidationTypes.phone;
            default:
              return 'text';
          }
        }
        return 'text';
      };

      // don`t render currency input if we have only one base currency in portal
      if (!hasMulticurrencyActive && property.name === DEAL_CURRENCY_FIELD_NAME) {
        return null;
      }
      return /*#__PURE__*/_jsx(FormControl, {
        required: currentField ? currentField.required : property.required,
        label: property.label,
        error: isError,
        validationMessage: isError && (crmObjectCreationFormErrorMessage || /*#__PURE__*/_jsx(FormattedMessage, {
          message: `associationsPanel.tabs.create.validation.${getErrorMessage()}`,
          options: {
            label: property.label
          }
        })),
        labelTooltip: tooltip,
        children: /*#__PURE__*/_jsx(FormInputs, {
          property: property,
          currentValue: fieldvalue,
          onFormValueChanged: associationModalActions.setField,
          disabled: disabled,
          objectType: singularTypeToPlural[crmObjectType],
          portalId: portalId,
          openUrl: openUrl,
          snippetsApiWrapper: apiWrapper.snippets,
          correlatedField: correlatedField,
          correlatedFieldValue: correlatedFieldEntity ? correlatedFieldEntity.value : '',
          prosemirrorTrackerProperties: prosemirrorTrackerProperties,
          hubspotUserId: hubspotUserId
        })
      }, property.name);
    }), /*#__PURE__*/_jsx(AssociationProperties, {})]
  });
}
export default CreateNewTab;